import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ProductPrice from "@/types/product-price";
import UserType from "@/types/user-type";
import { request } from "@/utils/request";
import { Button } from "@/components/button";

type SelectMembershipFormProps = {
  currentUser: UserType;
  productPrice: ProductPrice;
}

type SelectMembershipFormValues = {
  lookupKey: ProductPrice["lookupKey"];
  quantity: number;
}

const membershipTailwindClasses = {
  basic_monthly: {
    border: "border-primary-blue-400",
    banner: "bg-primary-blue-400",
    button: "text-white bg-primary-blue-400 hover:bg-primary-blue-500 disabled:bg-primary-blue-200 disabled:cursor-not-allowed"
  },

  premium_monthly: {
    border: "border-primary-green-600",
    banner: "bg-primary-green-600",
    button: "text-white bg-primary-green-600 hover:bg-primary-green-700 disabled:bg-primary-green-200 disabled:cursor-not-allowed"
  }
}

export const SelectMembershipForm = ({ currentUser, productPrice }: SelectMembershipFormProps) => {
  const { t } = useTranslation("select-membership-page")
  const { lookupKey } = productPrice
  const { border, banner, button } = membershipTailwindClasses[lookupKey]
  const highlights = t(`${lookupKey}.highlights`, { returnObjects: true }) as Array<string>;
  const currentlyActive = currentUser?.activeSubscriptions.some(
    subscription => subscription.items.some(
      item => item.productPrice.lookupKey === lookupKey
    )
  )
  const { handleSubmit, register, watch, formState: { isSubmitting } } = useForm<SelectMembershipFormValues>({
    defaultValues: {
      lookupKey,
      quantity: currentUser.requiredSeats
    }
  })
  const quantity = watch("quantity")

  const onSubmit = async (values: SelectMembershipFormValues) => {
    const response = await request.post <{ redirectUrl: string }>("/stripe/checkout/sessions", { ...values, cancelUrl: window.location.href })

    if (response.ok && response?.data?.redirectUrl) {
      window.location.href = response.data.redirectUrl;
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={`bg-white rounded-2xl border-4 ${border} w-[320px] pb-4 flex flex-col gap-4 text-center overflow-clip`}>
        <div className={`${banner} text-white py-2 text-2xl`}>{productPrice.product.name}</div>
        <div>
          {
            currentlyActive
              ? quantity
              : <input type="number" max="10" min="1" className="h-8 w-12 p-0 text-center rounded-md" {...register("quantity")} />
          }{" "}
          {t("seat", { count: quantity })} x <span className="text-xl">{productPrice.amount.formatted}</span> <i>{productPrice.interval.formatted}</i>
        </div>

        <div className="flex flex-col gap-4 px-4 items-start">
          <ul className="list-disc list-inside text-start">
            {
              highlights.map((highlight, index) => (
                <li key={`${lookupKey}-highlight-${index}`}>{highlight}</li>
              ))
            }
          </ul>

          <Button
            type="submit"
            className={`rounded-lg w-full px-4 py-3 ${button}`}
            loading={isSubmitting}
            disabled={currentlyActive}
          >
            {currentlyActive ? t("currentMembership") : t("getNow")}
          </Button>
        </div>

        <p>{t("cancelAnytime")}</p>
    </form>
  )
}
